import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RevisionStatus } from "shared/utils/enum";
import { ProjectListService } from "shared/services/project";
import { GetProjectsWithUnreadChatService } from "shared/services/notification";
import { handleErrors } from "shared/utils/helper";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./styles.module.scss";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomSearch from "shared/components/common/customSearch";
import FilterDropdown from "shared/components/common/filterDropdown";
import CustomProjectCard from "shared/components/common/customProjectCard";
import NoContentCard from "shared/components/common/noContentCard";
import ProjectCardSkeleton from "shared/components/common/projectCardSkeleton";
import Spinner from "shared/components/common/spinner";
import { resolveOnlyMyProjects } from "./resolveOnlyMyProjects";

interface ProjectsListProps {
  userType?: "designer" | "collaborator" | "builder" | "client" | "finalReviewer";
  pageTitle?: string;
  pageDescription?: string;
  showPageHeading?: boolean;
  showSearchFilter?: boolean;
  showNotificationBanner?: boolean;
  notificationBannerContent?: React.ReactNode;
  noContentMessage?: string;
  detailRoutePath: string;
  EnableApiIntegration?: boolean;
  staticProjects?: any[];
  limit?: number;
  showEmptyState?: boolean;
  containerClassName?: string;
  isLoading?: boolean;
  onProjectsLoaded?: (projects: any[]) => void;
  onlyMyProjects?: boolean;
  isArchived?: boolean;
  showArchiveButton?: boolean;
}

const ProjectsList = ({
  pageTitle,
  pageDescription,
  showPageHeading = true,
  showSearchFilter = false,
  showNotificationBanner = false,
  notificationBannerContent,
  noContentMessage = "You don't have any projects at the moment.",
  detailRoutePath,
  EnableApiIntegration = false,
  staticProjects = [],
  limit = 10,
  showEmptyState = true,
  containerClassName,
  isLoading = false,
  onProjectsLoaded,
  userType,
  onlyMyProjects = false,
  isArchived = false,
  showArchiveButton = false,
}: ProjectsListProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scopeToMyProjects = resolveOnlyMyProjects(
    onlyMyProjects,
    location.state,
  );
  const [loading, setLoading] = useState(EnableApiIntegration);
  const [loadingMore, setLoadingMore] = useState(false);
  const [projects, setProjects] = useState<any[]>(staticProjects);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [unreadChatIds, setUnreadChatIds] = useState<Set<number>>(new Set());

  const getInitialRevisionFilter = () => {
    const stateFilter = location.state?.filterRevisionStatus;
    if (stateFilter) {
      return stateFilter;
    }
    return "";
  };

  const [filterRevisionStatus, setFilterRevisionStatus] = useState(
    getInitialRevisionFilter(),
  );
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const hasInitialLoaded = useRef(false);
  const searchParamsRef = useRef({
    searchTerm,
    filterStatus,
    filterRevisionStatus,
    sortOrder,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const scrollStateRef = useRef({ currentPage, hasMore, loadingMore, loading });
  const isLoadingData = EnableApiIntegration ? loading : isLoading;

  useEffect(() => {
    scrollStateRef.current = { currentPage, hasMore, loadingMore, loading };
  }, [currentPage, hasMore, loadingMore, loading]);

  // Per-card unread chat dot: fetch the set of project IDs with unread
  // comment/reply notifications once, then keep it live via the global
  // notification socket event.
  useEffect(() => {
    if (!EnableApiIntegration) return;
    GetProjectsWithUnreadChatService()
      .then((r) => {
        const data = r.data?.data ?? r.data;
        const ids: number[] = (data?.project_ids ?? []).map((n: any) =>
          Number(n),
        );
        setUnreadChatIds(new Set(ids));
      })
      .catch(() => {});
  }, [EnableApiIntegration]);

  useEffect(() => {
    const handleNewNotification = (event: Event) => {
      const data = (event as CustomEvent<any>)?.detail;
      const type = String(data?.type || "").toLowerCase();
      const isChat = type === "new_comment" || type === "new_reply";
      const projectId = Number(data?.project_id);
      if (!isChat || !projectId) return;
      setUnreadChatIds((prev) => {
        if (prev.has(projectId)) return prev;
        const next = new Set(prev);
        next.add(projectId);
        return next;
      });
    };
    window.addEventListener("notification:new", handleNewNotification);
    return () => {
      window.removeEventListener("notification:new", handleNewNotification);
    };
  }, []);

  useEffect(() => {
    const stateFilter = location.state?.filterRevisionStatus;
    if (stateFilter && stateFilter !== filterRevisionStatus) {
      setFilterRevisionStatus(stateFilter);
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (EnableApiIntegration && !hasInitialLoaded.current) {
      // eslint-disable-line
      getProjects(1, true);
      hasInitialLoaded.current = true;
      searchParamsRef.current = {
        searchTerm,
        filterStatus,
        filterRevisionStatus,
        sortOrder,
      };
    }
  }, [EnableApiIntegration]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => {
      if (
        !hasInitialLoaded.current ||
        !EnableApiIntegration ||
        !showSearchFilter
      ) {
        return;
      }

      const hasChanged =
        searchParamsRef.current.searchTerm !== searchTerm ||
        searchParamsRef.current.filterStatus !== filterStatus ||
        searchParamsRef.current.filterRevisionStatus !== filterRevisionStatus ||
        searchParamsRef.current.sortOrder !== sortOrder;

      if (!hasChanged) {
        return;
      }

      searchParamsRef.current = {
        searchTerm,
        filterStatus,
        filterRevisionStatus,
        sortOrder,
      };
      setCurrentPage(1);
      setHasMore(true);

      const timeoutId = setTimeout(() => {
        getProjects(1, true);
      }, 500);

      return () => clearTimeout(timeoutId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      searchTerm,
      filterStatus,
      filterRevisionStatus,
      sortOrder,
      EnableApiIntegration,
      showSearchFilter,
    ],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!EnableApiIntegration) {
      setProjects(staticProjects);
      if (staticProjects.length > 0) {
        onProjectsLoaded?.(staticProjects);
      }
    }
  }, [staticProjects, EnableApiIntegration, onProjectsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const getProjects = async (page: number = 1, reset: boolean = false) => {
    if (!EnableApiIntegration || isFetchingRef.current) return;

    isFetchingRef.current = true;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await ProjectListService({
        page,
        limit,
        search: searchTerm.trim() || undefined,
        status: filterStatus || undefined,
        revision_status: filterRevisionStatus || undefined,
        sort_order: sortOrder,
        ...(scopeToMyProjects && { only_my_projects: true }),
        ...(isArchived && { is_archived: true }),
      });

      if (response.status === 200) {
        let apiProjects =
          response.data?.data?.projects ||
          response.data?.projects ||
          response.data ||
          [];

        if (isArchived) {
          apiProjects = apiProjects.filter(
            (p: any) => p.is_archived === true,
          );
        }

        const totalProjects =
          response.data?.data?.total || response.data?.total;
        if (totalProjects !== undefined) {
          const totalPages = Math.ceil(totalProjects / limit);
          setHasMore(page < totalPages);
        } else {
          setHasMore(apiProjects.length >= limit);
        }

        if (reset) {
          setProjects(apiProjects);
          onProjectsLoaded?.(apiProjects);
        } else {
          setProjects((prev) => {
            const updatedProjects = [...prev, ...apiProjects];
            onProjectsLoaded?.(updatedProjects);
            return updatedProjects;
          });
        }

        setCurrentPage(page);
      }
    } catch (error: any) {
      handleErrors(error);
      if (reset) {
        setProjects([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (!EnableApiIntegration || !containerRef.current) {
      return;
    }

    const container = containerRef.current;

    const handleScroll = () => {
      const state = scrollStateRef.current;

      if (
        !state.hasMore ||
        state.loadingMore ||
        state.loading ||
        isFetchingRef.current
      ) {
        return;
      }

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 300) {
        const currentState = scrollStateRef.current;
        if (
          currentState.hasMore &&
          !currentState.loadingMore &&
          !currentState.loading &&
          !isFetchingRef.current
        ) {
          getProjects(currentState.currentPage + 1, false);
        }
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [EnableApiIntegration]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={classNames(
        showPageHeading ? styles.pageWrapper : "h-full",
        showPageHeading ? "flex flex-col md:gap-6 gap-4 w-full h-full" : "",
      )}
    >
      {showPageHeading && pageTitle && (
        <div className="sm:pr-0 pr-12">
          <CustomPageHeading
            title={pageTitle}
            description={pageDescription || ""}
          />
        </div>
      )}

      {showNotificationBanner && notificationBannerContent}

      {showNotificationBanner && <h2>All Projects</h2>}

      {showSearchFilter && (
        <div
          className={classNames(
            styles.box,
            "flex lg:flex-row flex-col lg:items-center justify-between gap-4",
          )}
        >
          <div
            className={classNames(styles.searchContainer, "w-full lg:w-4/12")}
          >
            <CustomSearch
              placeholder="Search project by name"
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center lg:justify-end justify-start gap-2 lg:flex-nowrap flex-wrap lg:w-8/12 w-full">
            <FilterDropdown
              label="Project Status"
              icon={<Icons.Filter />}
              options={[
                "All",
                "Consultation",
                "Preliminary",
                "Preliminary Finals",
                "Final Review",
                "Completed",
              ]}
              selected={
                filterStatus
                  ? filterStatus
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")
                  : "All"
              }
              onSelect={(value: string) => {
                if (value === "All") {
                  setFilterStatus("");
                } else {
                  setFilterStatus(value.toLowerCase().replace(/ /g, "_"));
                }
              }}
            />
            <FilterDropdown
              label="Revision Status"
              icon={<Icons.Filter />}
              options={[
                "All",
                "In Progress",
                "Pending Client Feedback",
                "In Review",
                "Completed",
              ]}
              selected={
                filterRevisionStatus
                  ? (() => {
                      const statusMap: { [key: string]: string } = {
                        [RevisionStatus.IN_PROGRESS]: "In Progress",
                        [RevisionStatus.WAITING_FOR_CLIENT_RESPONSE]:
                          "Pending Client Feedback",
                        [RevisionStatus.IN_REVIEW]: "In Review",
                        [RevisionStatus.COMPLETED]: "Completed",
                      };
                      return (
                        statusMap[filterRevisionStatus] ||
                        filterRevisionStatus
                          .split("_")
                          .map(
                            (word: string) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(" ")
                      );
                    })()
                  : "All"
              }
              onSelect={(value: string) => {
                if (value === "All") {
                  setFilterRevisionStatus("");
                } else {
                  const statusMap: { [key: string]: string } = {
                    "In Progress": RevisionStatus.IN_PROGRESS,
                    "Pending Client Feedback":
                      RevisionStatus.WAITING_FOR_CLIENT_RESPONSE,
                    "In Review": RevisionStatus.IN_REVIEW,
                    Completed: RevisionStatus.COMPLETED,
                  };
                  setFilterRevisionStatus(
                    statusMap[value] || value.toLowerCase().replace(/ /g, "_"),
                  );
                }
              }}
            />
            <FilterDropdown
              label="Sort by"
              icon={<Icons.Sort />}
              options={["Newest", "Oldest"]}
              selected={sortOrder === "DESC" ? "Newest" : "Oldest"}
              onSelect={(value: string) => {
                setSortOrder(value === "Newest" ? "DESC" : "ASC");
              }}
            />
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        className={classNames(
          "flex flex-col gap-4 h-full",
          styles.projectsListContainer,
          containerClassName,
        )}
      >
        {isLoadingData ? (
          <ProjectCardSkeleton count={4} />
        ) : projects.length === 0 && showEmptyState ? (
          <div
            className={classNames(
              styles.noContentCardContainer,
              "flex items-center justify-center h-full",
            )}
          >
            <NoContentCard
              Icon={Icons.NoHome}
              customIconContianer={classNames(styles.noContentCardIcon)}
              label1="You're all set — no projects assigned yet"
            />
          </div>
        ) : projects.length > 0 ? (
          <>
            {projects.map((item: any, index: number) => (
              <CustomProjectCard
                key={item.id || item.project_id || index}
                id={item.id || item.project_id}
                status={item.status}
                image={item.project_asset?.path || item.image}
                title={item.name || item.title}
                currentRevision={item.latest_revision || item.currentRevision}
                viewerViewOnly={!!item.current_user_view_only}
                isDesignerJoined={item.is_designer_joined ?? item.is_joined}
                isArchived={item.is_archived ?? isArchived}
                hasUnreadChat={unreadChatIds.has(
                  Number(item.id || item.project_id),
                )}
                role={item.role}
                onArchiveToggle={() => getProjects(1, true)}
                onJoinSuccess={() => getProjects(1, true)}
                onLeaveSuccess={() => getProjects(1, true)}
                showArchiveButton={showArchiveButton}
                onPrimaryAction={() => {
                  navigate(
                    detailRoutePath.replace(
                      ":id",
                      String(item.id || item.project_id || index),
                    ),
                    {
                      state: { projectData: item },
                    },
                  );
                }}
              />
            ))}
            {loadingMore && (
              <div className="flex justify-center items-center py-8 min-h-[80px] w-full">
                <div className="flex flex-col items-center gap-2">
                  <Spinner />
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectsList;
